import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Award, TrendingUp, FileText, Plus, X, Send, Save, Pencil } from "lucide-react";

const STATUS_PILL = {
    draft: "bg-ink/5 text-ink/70 border-ink/10",
    submitted: "bg-yellow-50 text-yellow-700 border-yellow-200",
    dept_approved: "bg-blue-50 text-blue-700 border-blue-200",
    finalized: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
};

const todayPeriod = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function MyPerformance() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [criteria, setCriteria] = useState([]);
    const [maxScore, setMaxScore] = useState(10);
    const [editor, setEditor] = useState(null); // { mode: 'create'|'edit', review? }

    const load = async () => {
        try {
            const [list, settings] = await Promise.all([
                api.get("/performance").catch(() => ({ data: [] })),
                api.get("/company-settings").catch(() => ({ data: {} })),
            ]);
            setRows((list.data || []).filter((r) => !user?.employee_id || r.employee_id === user.employee_id));
            const c = settings.data?.performance_criteria || [];
            setCriteria(Array.isArray(c) && c.length ? c : []);
            setMaxScore(Number(settings.data?.performance_max_score || 10));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const finalized = rows.filter((r) => r.status === "finalized");
    const inProgress = rows.filter((r) => r.status !== "finalized" && r.status !== "rejected");
    const editable = rows.filter((r) => r.status === "draft" || r.status === "rejected");
    const latest = finalized[0];

    const openCreate = () => setEditor({ mode: "create" });
    const openEdit = (r) => setEditor({ mode: "edit", review: r });
    const closeEditor = () => setEditor(null);

    return (
        <div className="px-5 sm:px-8 md:px-12 py-8 sm:py-12 md:py-16" data-testid="my-performance-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Self-service</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02] flex items-center gap-3">
                        <Award className="w-9 h-9 text-klein" strokeWidth={1.5} /> My Performance
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        Self-assess yourself for the current period — your Department Lead will then comment and approve, and HR finalizes the score.
                    </p>
                </div>
                {user?.employee_id && (
                    <button
                        onClick={openCreate}
                        data-testid="my-perf-new-review-btn"
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                    >
                        <Plus className="w-4 h-4" /> New self-review
                    </button>
                )}
            </div>

            {!loading && finalized.length > 0 && latest && (
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10" data-testid="my-performance-kpis">
                    <KPI label="Latest score" value={`${latest.normalized_score ?? "—"} / 100`} icon={TrendingUp} accent />
                    <KPI label="Latest rating" value={latest.rating || "—"} icon={Award} />
                    <KPI label="Period" value={latest.period || "—"} icon={FileText} />
                </div>
            )}

            {loading && <div className="mt-10 text-sm text-ink/40">Loading…</div>}

            {!loading && rows.length === 0 && (
                <div className="mt-10 bg-white border border-ink/10 p-12 text-center text-sm text-ink/40" data-testid="my-performance-empty">
                    No performance reviews on file yet. Start a self-review for the current period above.
                </div>
            )}

            {!loading && rows.length > 0 && (
                <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                    <ReviewList title="In progress" items={inProgress} testid="my-performance-in-progress" emptyText="Nothing in progress." onEdit={openEdit} editableIds={editable.map((r) => r.id)} />
                    <ReviewList title="Finalized" items={finalized} testid="my-performance-finalized" emptyText="No finalized reviews yet." />
                </div>
            )}

            {editor && (
                <ReviewEditor
                    mode={editor.mode}
                    review={editor.review}
                    criteria={criteria}
                    maxScore={maxScore}
                    employeeId={user?.employee_id}
                    onClose={closeEditor}
                    onSaved={async () => { await load(); closeEditor(); }}
                />
            )}
        </div>
    );
}

function KPI({ label, value, icon: Icon, accent }) {
    return (
        <div className={`p-6 ${accent ? "bg-klein text-white" : "bg-white"}`}>
            <div className="flex items-start justify-between">
                <div className={`overline ${accent ? "!text-white/70" : ""}`}>{label}</div>
                <div className={`w-8 h-8 flex items-center justify-center ${accent ? "bg-white/15" : "border border-ink/10 text-ink/60"}`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
            <div className="mt-6 font-display font-black text-3xl tracking-tighter tabular-nums truncate">{value}</div>
        </div>
    );
}

function ReviewList({ title, items, testid, emptyText, onEdit, editableIds = [] }) {
    const isEditable = (id) => editableIds.includes(id);
    return (
        <div className="bg-white p-6" data-testid={testid}>
            <div className="overline">{title}</div>
            <h3 className="mt-1 font-display font-bold text-xl tracking-tight text-ink">{items.length} review{items.length === 1 ? "" : "s"}</h3>
            {items.length === 0 ? (
                <div className="mt-4 text-sm text-ink/40 py-4 border-t border-ink/5">{emptyText}</div>
            ) : (
                <ul className="mt-4 space-y-1">
                    {items.map((r) => (
                        <li key={r.id} className="flex items-center justify-between py-3 border-b border-ink/5 text-sm gap-4" data-testid={`my-perf-row-${r.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="text-ink">{r.period || "—"}</div>
                                <div className="text-xs text-ink/50 truncate">
                                    Reviewer: {r.reviewer_name || r.evaluator || "—"}{r.normalized_score != null ? ` · ${r.normalized_score}/100` : ""}{r.rating ? ` · ${r.rating}` : ""}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${STATUS_PILL[r.status] || "bg-ink/5 text-ink/70 border-ink/10"}`}>
                                    {(r.status || "").replace(/_/g, " ")}
                                </span>
                                {onEdit && isEditable(r.id) && (
                                    <button
                                        onClick={() => onEdit(r)}
                                        data-testid={`my-perf-edit-${r.id}`}
                                        className="inline-flex items-center gap-1 text-xs underline text-klein hover:text-ink"
                                    >
                                        <Pencil className="w-3 h-3" /> Edit
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ReviewEditor({ mode, review, criteria, maxScore, employeeId, onClose, onSaved }) {
    const effectiveCriteria = useMemo(() => {
        const fromReview = review?.criteria_snapshot;
        if (Array.isArray(fromReview) && fromReview.length) return fromReview;
        return criteria || [];
    }, [review, criteria]);
    const effectiveMax = Number(review?.max_score || maxScore || 10);

    const [period, setPeriod] = useState(review?.period || todayPeriod());
    const [scores, setScores] = useState(() => {
        const init = {};
        for (const c of effectiveCriteria) init[c.key] = Number(review?.scores?.[c.key] ?? 0);
        return init;
    });
    const [comments, setComments] = useState(review?.manager_comments || "");
    const [saving, setSaving] = useState(false);

    const totalWeight = useMemo(() => effectiveCriteria.reduce((s, c) => s + Number(c.weight || 0), 0), [effectiveCriteria]);
    const weightedTotal = useMemo(() => {
        return effectiveCriteria.reduce((s, c) => s + (Number(scores[c.key] || 0) * Number(c.weight || 0)), 0);
    }, [effectiveCriteria, scores]);
    const maxTotal = effectiveMax * (totalWeight || 1);
    const normalized = maxTotal ? Math.round((weightedTotal / maxTotal) * 1000) / 10 : 0;

    const setScore = (key, val) => {
        const n = Math.max(0, Math.min(effectiveMax, Number(val) || 0));
        setScores((p) => ({ ...p, [key]: n }));
    };

    const save = async (submit = false) => {
        if (!employeeId) {
            toast.error("Your account is not linked to an employee record.");
            return;
        }
        if (!period.trim()) {
            toast.error("Period is required (e.g. 2026-04 or 2026-H1).");
            return;
        }
        try {
            setSaving(true);
            let saved;
            if (mode === "create") {
                const { data } = await api.post("/performance", {
                    employee_id: employeeId,
                    period: period.trim(),
                    scores,
                    manager_comments: comments,
                });
                saved = data;
            } else {
                const { data } = await api.put(`/performance/${review.id}`, { scores, manager_comments: comments });
                saved = data;
            }
            if (submit && saved?.id) {
                await api.put(`/performance/${saved.id}/submit`, {});
                toast.success("Self-review submitted to your Department Lead.");
            } else {
                toast.success(mode === "create" ? "Draft created." : "Draft updated.");
            }
            await onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" data-testid="my-perf-editor">
            <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-ink/10 shadow-xl">
                <div className="flex items-center justify-between p-5 border-b border-ink/10">
                    <div>
                        <div className="overline">{mode === "create" ? "New self-review" : "Edit self-review"}</div>
                        <h3 className="mt-1 font-display font-bold text-xl tracking-tight text-ink">
                            Score yourself across the criteria
                        </h3>
                    </div>
                    <button onClick={onClose} data-testid="my-perf-editor-close" className="text-ink/60 hover:text-ink"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-5 space-y-5">
                    <div>
                        <label className="block text-xs uppercase tracking-[0.12em] text-ink/60 mb-1">Period</label>
                        <input
                            type="text"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            disabled={mode === "edit"}
                            placeholder="e.g. 2026-04 or 2026-H1"
                            data-testid="my-perf-period-input"
                            className="w-full border border-ink/15 px-3 py-2 text-sm disabled:bg-ink/5"
                        />
                    </div>

                    {effectiveCriteria.length === 0 ? (
                        <div className="text-sm text-ink/50 border border-dashed border-ink/15 p-4">
                            No performance criteria configured yet. Ask HR to set them up in Company Settings.
                        </div>
                    ) : (
                        <div className="border border-ink/10">
                            <div className="grid grid-cols-12 px-3 py-2 bg-ink/5 text-[11px] uppercase tracking-[0.12em] text-ink/60">
                                <div className="col-span-6">Criterion</div>
                                <div className="col-span-2 text-right">Weight</div>
                                <div className="col-span-4 text-right">Your score (0–{effectiveMax})</div>
                            </div>
                            {effectiveCriteria.map((c) => (
                                <div key={c.key} className="grid grid-cols-12 items-center px-3 py-3 border-t border-ink/5 text-sm">
                                    <div className="col-span-6">
                                        <div className="text-ink">{c.label || c.key}</div>
                                        {c.description && <div className="text-xs text-ink/50 mt-0.5">{c.description}</div>}
                                    </div>
                                    <div className="col-span-2 text-right tabular-nums text-ink/60">{c.weight}</div>
                                    <div className="col-span-4">
                                        <input
                                            type="number"
                                            min={0}
                                            max={effectiveMax}
                                            step="0.5"
                                            value={scores[c.key] ?? 0}
                                            onChange={(e) => setScore(c.key, e.target.value)}
                                            data-testid={`my-perf-score-${c.key}`}
                                            className="w-full border border-ink/15 px-2 py-1.5 text-sm text-right tabular-nums"
                                        />
                                    </div>
                                </div>
                            ))}
                            <div className="grid grid-cols-12 px-3 py-3 border-t border-ink/10 bg-ink/5 text-sm">
                                <div className="col-span-8 font-medium">Normalized score</div>
                                <div className="col-span-4 text-right font-display font-bold tabular-nums">{normalized.toFixed(1)} / 100</div>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs uppercase tracking-[0.12em] text-ink/60 mb-1">Your comments / context (optional)</label>
                        <textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={3}
                            data-testid="my-perf-comments-input"
                            placeholder="Highlight achievements, blockers or context for your Department Lead."
                            className="w-full border border-ink/15 px-3 py-2 text-sm"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-5 border-t border-ink/10 bg-ink/[0.02]">
                    <button onClick={onClose} disabled={saving} className="text-sm text-ink/60 hover:text-ink px-4 py-2">Cancel</button>
                    <button
                        onClick={() => save(false)}
                        disabled={saving || effectiveCriteria.length === 0}
                        data-testid="my-perf-save-draft-btn"
                        className="inline-flex items-center gap-2 border border-ink/15 text-ink px-4 py-2 text-sm hover:bg-ink/5 disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" /> Save draft
                    </button>
                    <button
                        onClick={() => save(true)}
                        disabled={saving || effectiveCriteria.length === 0}
                        data-testid="my-perf-submit-btn"
                        className="inline-flex items-center gap-2 bg-klein text-white px-4 py-2 text-sm hover:bg-klein/90 disabled:opacity-50"
                    >
                        <Send className="w-4 h-4" /> Submit for approval
                    </button>
                </div>
            </div>
        </div>
    );
}
